import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Tab, Tabs } from '@mui/material';
import WorkQueues from 'enums/workQueue.enum';
import useRoles from 'common/hooks/useRoles';
import { shipmentModuleActions } from '../../../store/slices/shipmentModuleSlice';
import { workQueueActions } from '../../../store/slices/workQueueSlice';
import { WorkQueueTabSwitchProvider } from '../../WorkQueue/contexts/WorkQueueTabSwitchContext';
import ShipmentsView from './ShipmentsView';
import ReconcileShipment from '../../WorkQueue/components/ReconcileShipment';
import ShipstationShipmentsView from './ShipstationShipmentsView';

/**
 * Wrapper for ReconcileShipment that sets the workQueue.preset to RECONCILE_SHIPMENT
 * so the component's internal preset guard passes, and provides the WorkQueueTabSwitchContext
 * that ReconcileShipment depends on.
 *
 * Since this Shipments module and the WorkQueue module are on different routes,
 * they will never be mounted simultaneously, so setting workQueue.preset is safe.
 */
const EmbeddedReconcileShipment = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(workQueueActions.loadSelectedWorkQueues(WorkQueues.RECONCILE_SHIPMENT));
  }, [dispatch]);

  return (
    <WorkQueueTabSwitchProvider>
      <ReconcileShipment />
    </WorkQueueTabSwitchProvider>
  );
};

const ShipmentsPage = () => {
  const dispatch = useDispatch();
  const { activeTab } = useSelector(({ shipmentModule }) => shipmentModule);
  const { viewShipments, reconcileShipment } = useRoles();

  // Ensure user is on a tab they have access to; default to first allowed tab
  useEffect(() => {
    const allowedTabs = [
      viewShipments && 'shipments',
      viewShipments && 'shipstation',
      reconcileShipment && 'reconcile',
    ].filter(Boolean);
    const isCurrentAllowed = allowedTabs.length > 0 && allowedTabs.includes(activeTab);
    if (!isCurrentAllowed && allowedTabs.length > 0) {
      dispatch(shipmentModuleActions.setActiveTab(allowedTabs[0]));
    }
  }, [viewShipments, reconcileShipment, activeTab, dispatch]);

  const handleTabChange = (event, newValue) => {
    dispatch(shipmentModuleActions.setActiveTab(newValue));
  };

  const canViewShipments = viewShipments;
  const canViewReconcile = reconcileShipment;
  const canViewShipstation = viewShipments;
  const showTabs = canViewShipments || canViewReconcile || canViewShipstation;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {showTabs && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            {canViewShipments && <Tab label='Shipments' value='shipments' />}
            {canViewShipstation && <Tab label='Shipstation Shipments' value='shipstation' />}
            {canViewReconcile && <Tab label='Reconcile Shipments' value='reconcile' />}
          </Tabs>
        </Box>
      )}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {activeTab === 'shipments' && canViewShipments && <ShipmentsView />}
        {activeTab === 'reconcile' && canViewReconcile && <EmbeddedReconcileShipment />}
        {activeTab === 'shipstation' && canViewShipstation && <ShipstationShipmentsView />}
      </Box>
    </Box>
  );
};

export default ShipmentsPage;
